/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Icon, Popup, Tooltip, VerticalMenu } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import React, { useContext, useEffect, useState } from 'react';
import descriptors from '../../locales/en-US/descriptors';
import {
  IAccountStatus,
  IUtilityServiceAccountTableProps
} from './IUtilityServiceSettingsProps';
import {
  getPlugStatusMessageDescription,
  getUtilityAccountMessageDescription,
  getUtilityAccountMessageKey,
  getUtilityAccountStatusImage,
  getUtilityImageElemement,
  isDeactivateAvailabel,
  isDeactivateIDSAvailable,
  isDeactivedAction,
  isDeactivedInProgressAction,
  isEnableIDSAvailable,
  isFailedLogin,
  isIDSConnectionError,
  isIntegrationTypeAvailabel,
  isReactivateIDSAvailable,
  isUpdateAction,
  isUtilityProviderEnrolled
} from '../shared/Utils/Utils';
import { useUtilityAccountContext } from '../../context/UtilityAccountContext/UtilityAccountContext';
import UtilityAccountAction from '../../context/UtilityAccountContext/UtilityAccountActions';
import { useTranslation } from '@scuf/localization';
import { IAny } from '../DashboardKPIWidgets/IEnergyManagementProps';
import ElectricityIconUtility from '../../images/ElectricityIconUtility.svg';
import { UtilityType } from '../shared/Utils/Constants';
import UtilityServiceManageBillContainer from './UtilityServiceManageBillContainer';
import { ApiContext } from '../../context/ApiContext';
import TruncateText from '../shared/TruncatedText/TruncateText';

const UtilityServiceAccountTable = (
  props: IUtilityServiceAccountTableProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data: apiData,
    data,
    bearerToken,
    requestContext,
    parentPutAxios
  } = props;
  const emaasContext = useContext(ApiContext);
  const [newdata, setNewData] = useState(props.data);
  const [openConvertToAuto, setOpenConvertToAuto] = useState<boolean>(false);
  function createnewdata(data: any) {
    const accMap = new Map();
    data.forEach((item: any) => {
      const { accountNumber, meterNumber, meterName, ...rest } = item;
      if (accMap.has(accountNumber)) {
        accMap.get(accountNumber).meters.push({ meterNumber, meterName });
      } else {
        accMap.set(accountNumber, {
          accountNumber,
          meters: [{ meterNumber, meterName }],
          ...rest
        });
      }
    });
    return Array.from(accMap.values());
  }

  useEffect(() => {
    setNewData(createnewdata(props.data));
  }, [props.data]);

  const [, dispatch] = useUtilityAccountContext();
  const showModal = () => dispatch(UtilityAccountAction.showUtilityModal());
  const ShowConversionModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showConversionModal());
  };

  const editDetailsModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.editDetailsModal(celldata.rowData));
  };

  const showDeactivateAccountModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showDeactivateAccountModal());
  };
  const showReactivateAccountModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showReactivateAccountModal());
  };
  const showDeactivateIDSModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showDeactivateIDSModal());
  };
  const showReactivateIDSModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showReactivateIDSModal());
  };
  const handleActions = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showReverifyModal());
  };
  const handleUtilityProviderNameClick = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showMeterNameChangeModal());
  };
  const ShowEnableIDSModal = (celldata: ICellData) => {
    dispatch(UtilityAccountAction.setSelectedUtilityInfo(celldata.rowData));
    dispatch(UtilityAccountAction.showEnableIDSModal());
  };
  const renderServiceProviderName = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { providerName, status } = accountStatusRow;
    const isLinkEnabled = isUtilityProviderEnrolled(status);
    return (
      <div>
        {isLinkEnabled ? (
          <Button
            type="link"
            content={providerName}
            onClick={() => handleUtilityProviderNameClick(celldata)}
            className="provider-name-cell"
          >
            <TruncateText
              fromUtility={true}
              text={providerName}
              maxLength={15}
            />
          </Button>
        ) : (
          <p className="provider-name-cell">
            <TruncateText
              fromUtility={true}
              text={providerName}
              maxLength={15}
            />
          </p>
        )}
      </div>
    );
  };
  const [expandedRows, setexpandedRows] = useState<IAny>([apiData]);

  const rowExpansionTemplate = (data: IAny) => {
    const { utilityType } = data;
    const imgElement = getUtilityImageElemement(utilityType);
    return (
      <div className="Account-Table">
        {data.meterDetails && data.meterDetails.length > 0 ? (
          data.meterDetails.map((meter: any, index: any) => (
            <div className="Account-Table-Meterdetails" key={index}>
              <span className="Account-Table-Meterdetails-Icon">
                {imgElement}
              </span>
              {meter.meterNumber === null
                ? `${meter.meterName} ( N/A )`
                : `${meter.meterName} (${meter.meterNumber})`}
            </div>
          ))
        ) : (
          <div>no meters</div>
        )}
      </div>
    );
  };
  const expanderTemplate = (
    data: ICellData,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const open =
      expandedRows.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data.rowData)
      ) >= 0;
    return data.rowData.meterDetails && data.rowData.meterDetails.length > 0 ? (
      <div
        className="toggle-expandedrow-accnumber"
        onClick={() =>
          toggleExpand(data.rowData, open, expandedRows, setexpandedRows)
        }
      >
        <Icon
          root="common"
          size="small"
          name={open ? 'caret-down' : 'caret-right'}
        />
        <TruncateText text={data.value} maxLength={15} />
      </div>
    ) : (
      <div>
        <TruncateText text={data.rowData.accountNumber} maxLength={15} />
      </div>
    );
  };
  const toggleExpand = (
    data: IAny,
    open: boolean,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const expanded = [...expandedRows];
    if (open) {
      let index = expanded.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data)
      );
      expanded.splice(index, 1);
    } else {
      expanded.push(data);
    }
    setexpandedRows(expanded);
  };

  const renderAccountNumber = (cellData: ICellData) => {
    const accountStatusRow: IAccountStatus = cellData.rowData;
    const { accountNumber, meterNumber } = accountStatusRow;
    return (
      <span>{expanderTemplate(cellData, expandedRows, setexpandedRows)}</span>
    );
  };
  const renderUtilityType = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { utilityType } = accountStatusRow;
    const imgElement = getUtilityImageElemement(utilityType);
    return (
      <span>
        <span className="score-category-Badge">{imgElement}</span>
        <span>{utilityType}</span>
      </span>
    );
  };
  const renderIntegrationType = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { integrationType } = accountStatusRow;
    return (
      <span>
        <span>{integrationType}</span>
      </span>
    );
  };
  const renderUtilityStatus = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const { status } = accountStatusRow;
    const imageElement = getUtilityAccountStatusImage(status);
    const messageKey = getUtilityAccountMessageKey(status);
    const messageDescription = getUtilityAccountMessageDescription(status);
    return (
      <span>
        <span className="status-image">{imageElement}</span>
        <span>{t(messageKey)}</span>
        {
          // messageKey !== descriptors.urjanetStatus.ENROLLED &&
          //   messageKey !== descriptors.urjanetStatus.Active &&
          //   messageKey !== descriptors.urjanetStatus.DEACTIVATED && (
          <Tooltip
            element={
              <span className="service-account-status-tootip">
                <Icon name="badge-info" root="common" size="small" />
              </span>
            }
            content={t(messageDescription)}
            position="bottom center"
          />
          //)
        }
      </span>
    );
  };

  const renderIDSStatus = (celldata: ICellData) => {
    const accountStatusRow: IAccountStatus = celldata.rowData;
    const {
      utilityType,
      idsStatus,
      idsStatusDetail,
      integrationType
    } = accountStatusRow;
    const imageElement =
      idsStatus && integrationType === 'Automatic' ? (
        getUtilityAccountStatusImage(idsStatus)
      ) : (
        <></>
      );
    const messageKey =
      idsStatus && integrationType === 'Automatic'
        ? getUtilityAccountMessageKey(idsStatus)
        : '';
    const messageDescription =
      idsStatus && integrationType === 'Automatic'
        ? getPlugStatusMessageDescription(idsStatus, idsStatusDetail || '')
        : '';
    return utilityType == 'Electricity' &&
      integrationType === 'Automatic' &&
      idsStatus ? (
      <span>
        <span className="status-image">{imageElement}</span>
        <span>{t(messageKey)}</span>
        {
          <Tooltip
            element={
              <span className="service-account-status-tootip">
                <Icon name="badge-info" root="common" size="small" />
              </span>
            }
            content={t(messageDescription)}
            position="bottom center"
          />
        }
      </span>
    ) : (
      <>{'N/A'}</>
    );
  };

  const handleManageBill = (celldata: ICellData) => {
    emaasContext.setUtilityCellData(celldata);
    return emaasContext.onManageBill({
      cellData: celldata.rowData,
      isManageBill: true
    });
  };

  const handleManageTariffDetails = (celldata: ICellData) => {
    emaasContext.setUtilityCellData(celldata);
    return emaasContext.onManageTariffDetails({
      cellData: celldata.rowData,
      isManageTariffDetails: true
    });
  };

  const handleEditBill = (celldata: ICellData) => {
    emaasContext.setUtilityCellData(celldata);
    return emaasContext.onEditBill({
      cellData: celldata.rowData
    });
  };

  const getIDSPopupAction = (cellData: ICellData) => {
    const _allowIDSOptions =
      emaasContext &&
      emaasContext.enableIDSEnrollment &&
      cellData.rowData.integrationType === 'Automatic' &&
      cellData.rowData.utilityType === 'Electricity';
    const _renderEnableIdsOption = (_celldata: ICellData) => {
      return _allowIDSOptions &&
        isEnableIDSAvailable(
          _celldata.rowData.status,
          _celldata.rowData.idsStatus
        ) ? (
        <VerticalMenu.Item onClick={() => ShowEnableIDSModal(_celldata)}>
          {t(d.ENABLEIDS)}
        </VerticalMenu.Item>
      ) : null;
    };
    const _renderDeactivateIdsOption = (_celldata: ICellData) => {
      return _allowIDSOptions &&
        _celldata.rowData.idsStatus &&
        isDeactivateIDSAvailable(_celldata.rowData.idsStatus) ? (
        <VerticalMenu.Item onClick={() => showDeactivateIDSModal(_celldata)}>
          {t(d.DEACTIVATEIDSACCOUNT)}
        </VerticalMenu.Item>
      ) : null;
    };
    const _renderReactivateIdsOption = (_celldata: ICellData) => {
      return _allowIDSOptions &&
        _celldata.rowData.idsStatus &&
        isReactivateIDSAvailable(_celldata.rowData.idsStatus) ? (
        <VerticalMenu.Item onClick={() => showReactivateIDSModal(_celldata)}>
          {t(d.REACTIVATEIDSACCOUNT)}
        </VerticalMenu.Item>
      ) : null;
    };
    const _renderReverifyIDSOption = (_celldata: ICellData) => {
      return _allowIDSOptions &&
        _celldata.rowData.idsStatus &&
        isIDSConnectionError(
          _celldata.rowData.idsStatus,
          _celldata.rowData.idsStatusDetail || ''
        ) ? (
        <VerticalMenu.Item onClick={() => handleActions(_celldata)}>
          {t(d.REVERIFY)}
        </VerticalMenu.Item>
      ) : null;
    };
    return (
      _renderEnableIdsOption(cellData) ||
      _renderDeactivateIdsOption(cellData) ||
      _renderReactivateIdsOption(cellData) ||
      _renderReverifyIDSOption(cellData)
    );
  };

  const renderUtilityAction = (celldata: ICellData) => {
    // if (isUpdateAction(celldata.rowData.status)) {
    //   return (
    //     <Button
    //       type="link"
    //       content={t(d.UPDATE)}
    //       onClick={() => handleActions(celldata)}
    //       className="utility-action update"
    //     ></Button>
    //   );
    // }
    const _allowIDSOptions =
      emaasContext &&
      emaasContext.enableIDSEnrollment &&
      celldata.rowData.integrationType === 'Automatic' &&
      celldata.rowData.utilityType === 'Electricity';
    if (isDeactivedAction(celldata.rowData.status)) {
      return (
        <>
          <Popup
            className={`emaas-utility-popup ${
              _allowIDSOptions ? 'utility-popup-ids' : ''
            }`}
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              <VerticalMenu.Item
                onClick={() => showReactivateAccountModal(celldata)}
              >
                {t(d.REENROLL)}
              </VerticalMenu.Item>
              {getIDSPopupAction(celldata)}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else if (isFailedLogin(celldata.rowData.status)) {
      return (
        <>
          <Popup
            className="emaas-utility-popup"
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              {celldata.rowData.meterDetails &&
                celldata.rowData.meterDetails.length > 0 && (
                  <VerticalMenu.Item onClick={() => handleManageBill(celldata)}>
                    {t(d.MANAGEBILLDETAILS)}
                  </VerticalMenu.Item>
                )}
              <VerticalMenu.Item onClick={() => handleActions(celldata)}>
                {t(d.UPDATE)}
              </VerticalMenu.Item>
              {/* IDS Scenario to be handled */}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else if (isDeactivedInProgressAction(celldata.rowData.status)) {
      return (
        <>
          <Popup
            className={`emaas-utility-popup ${
              _allowIDSOptions ? 'utility-popup-ids' : ''
            }`}
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              {celldata.rowData.meterDetails &&
                celldata.rowData.meterDetails.length > 0 && (
                  <VerticalMenu.Item onClick={() => handleManageBill(celldata)}>
                    {t(d.MANAGEBILLDETAILS)}
                  </VerticalMenu.Item>
                )}
              {getIDSPopupAction(celldata)}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else if (isIntegrationTypeAvailabel(celldata.rowData.integrationType)) {
      return (
        <>
          <Popup
            className={`emaas-utility-popup ${
              _allowIDSOptions ? 'utility-popup-ids' : ''
            }`}
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              {// !['Sewer', 'Water'].includes(celldata.rowData.utilityType) &&
              celldata.rowData.meterDetails &&
                celldata.rowData.meterDetails.length > 0 &&
                (celldata.rowData.utilityType === 'Electricity' ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext &&
                    emaasContext.toggleGasManageBill) ||
                  ((celldata.rowData.utilityType === 'Sewer' ||
                    celldata.rowData.utilityType === 'Water') &&
                    emaasContext &&
                    emaasContext.toggleManualWaterSewer) ||
                  (celldata.rowData.utilityType === 'Water' &&
                    emaasContext &&
                    emaasContext.toggleManualWater)) && (
                  <VerticalMenu.Item onClick={() => handleManageBill(celldata)}>
                    {t(d.MANAGEBILLDETAILS)}
                  </VerticalMenu.Item>
                )}

              {// Manage Tariff Details for automatic integration type
              celldata.rowData.utilityType === 'Electricity' &&
                (celldata.rowData.status === 'Active' ||
                  celldata.rowData.status === 'PRODUCTION') &&
                emaasContext &&
                emaasContext.toggleManageTariffDetails && (
                  <VerticalMenu.Item
                    onClick={() => handleManageTariffDetails(celldata)}
                  >
                    {t(d.MANAGETARIFFDETAILS)}
                  </VerticalMenu.Item>
                )}

              {((emaasContext &&
                emaasContext.toggleEditManualAccount &&
                (celldata.rowData.integrationType === 'Manual' ||
                  (emaasContext.enableAutomaticEditAccount &&
                    celldata.rowData.integrationType === 'Automatic' &&
                    celldata.rowData.status === 'PRODUCTION')) &&
                (celldata.rowData.utilityType === 'Electricity' ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext.toggleGasManageBill) ||
                  ((celldata.rowData.utilityType === 'Sewer' ||
                    celldata.rowData.utilityType === 'Water') &&
                    emaasContext.toggleManualWaterSewer))) ||
                (celldata.rowData.utilityType === 'Water' &&
                  emaasContext.toggleManualWater)) && (
                //celldata.rowData.status !== 'NOMINATED' &&
                <VerticalMenu.Item
                  onClick={() => {
                    //handleEditBill(celldata)
                    editDetailsModal(celldata);
                  }}
                  className="unset-text-overflow"
                >
                  <span className="ellipsis-text">
                    {t(d.EDITACCOUNTDETAILS)}
                  </span>
                  <Tooltip
                    element={
                      <Icon root="common" name="badge-info" size="small" />
                    }
                    event="hover"
                    header={''}
                    content={
                      celldata.rowData.integrationType === 'Automatic'
                        ? t(d.EDITACCOUNTDETAILSTOOLTIPFORAUTOMATIC)
                        : t(d.EDITACCOUNTDETAILSTOOLTIP)
                    }
                    position="top right"
                    size="small"
                  />
                </VerticalMenu.Item>
              )}

              {celldata.rowData.status === 'FAILED_TO_ACCESS' && (
                <VerticalMenu.Item onClick={() => handleActions(celldata)}>
                  {t(d.UPDATE)}
                </VerticalMenu.Item>
              )}
              {celldata.rowData.integrationType === 'Automatic' &&
                celldata.rowData.status !== 'NOMINATED' && (
                  <VerticalMenu.Item
                    onClick={() => showDeactivateAccountModal(celldata)}
                  >
                    {emaasContext && emaasContext.enableIDSEnrollment
                      ? t(d.DEACTIVATEUDSACCOUNT)
                      : t(d.DEACTIVATEACCOUNT)}
                  </VerticalMenu.Item>
                )}
              {emaasContext &&
                ((celldata.rowData.utilityType === 'Electricity' &&
                  emaasContext.utilityBuilderManualToAutomaticToggle) ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext.convertToAutomaticForGas)) &&
                //   ||
                // celldata.rowData.utilityType === 'Sewer' ||
                //   (celldata.rowData.utilityType === 'Water' &&
                //     emaasContext.toggleManualWaterSewer)
                celldata.rowData.integrationType === 'Manual' &&
                celldata.rowData.status === 'Active' && (
                  <VerticalMenu.Item
                    onClick={() => ShowConversionModal(celldata)}
                  >
                    {t(d.CONVERTINTRGRATIONTYPE)}
                  </VerticalMenu.Item>
                )}
              {getIDSPopupAction(celldata)}

              {/* <VerticalMenu.Item>{t(d.DELETE)}</VerticalMenu.Item> */}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else if (isDeactivateAvailabel(celldata.rowData.status)) {
      return (
        <>
          <Popup
            className={`emaas-utility-popup ${
              _allowIDSOptions ? 'utility-popup-ids' : ''
            }`}
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              {// !['Sewer', 'Water'].includes(celldata.rowData.utilityType) &&
              celldata.rowData.meterDetails &&
                celldata.rowData.meterDetails.length > 0 &&
                (celldata.rowData.utilityType === 'Electricity' ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext &&
                    emaasContext.toggleGasManageBill) ||
                  ((celldata.rowData.utilityType === 'Sewer' ||
                    celldata.rowData.utilityType === 'Water') &&
                    emaasContext &&
                    emaasContext.toggleManualWaterSewer) ||
                  (celldata.rowData.utilityType === 'Water' &&
                    emaasContext &&
                    emaasContext.toggleManualWater)) && (
                  <VerticalMenu.Item onClick={() => handleManageBill(celldata)}>
                    {t(d.MANAGEBILLDETAILS)}
                  </VerticalMenu.Item>
                )}
              {emaasContext &&
                ((celldata.rowData.utilityType === 'Electricity' &&
                  emaasContext.utilityBuilderManualToAutomaticToggle) ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext.convertToAutomaticForGas)) &&
                //    ||
                // celldata.rowData.utilityType === 'Sewer' ||
                //   (celldata.rowData.utilityType === 'Water' &&
                //     emaasContext.toggleManualWaterSewer)
                celldata.rowData.integrationType === 'Manual' &&
                celldata.rowData.status === 'Active' && (
                  <VerticalMenu.Item
                    onClick={() => ShowConversionModal(celldata)}
                  >
                    {t(d.CONVERTINTRGRATIONTYPE)}
                  </VerticalMenu.Item>
                )}
              {// Manage Tariff Details for manual integration type
              celldata.rowData.utilityType === 'Electricity' &&
                (celldata.rowData.status === 'Active' ||
                  celldata.rowData.status === 'PRODUCTION') &&
                emaasContext &&
                emaasContext.toggleManageTariffDetails && (
                  <VerticalMenu.Item
                    onClick={() => handleManageTariffDetails(celldata)}
                  >
                    {t(d.MANAGETARIFFDETAILS)}
                  </VerticalMenu.Item>
                )}
              {(emaasContext &&
                emaasContext.toggleEditManualAccount &&
                (celldata.rowData.integrationType === 'Manual' ||
                  (emaasContext.enableAutomaticEditAccount &&
                    celldata.rowData.integrationType === 'Automatic' &&
                    celldata.rowData.status === 'PRODUCTION')) &&
                (celldata.rowData.utilityType === 'Electricity' ||
                  (celldata.rowData.utilityType === 'Gas' &&
                    emaasContext.toggleGasManageBill) ||
                  ((celldata.rowData.utilityType === 'Sewer' ||
                    celldata.rowData.utilityType === 'Water') &&
                    emaasContext.toggleManualWaterSewer))) ||
                (celldata.rowData.utilityType === 'Water' &&
                  emaasContext.toggleManualWater && (
                    //celldata.rowData.status !== 'NOMINATED' &&
                    <VerticalMenu.Item
                      onClick={() => {
                        //handleEditBill(celldata)
                        editDetailsModal(celldata);
                      }}
                    >
                      {t(d.EDITACCOUNTDETAILS)}
                      <Tooltip
                        element={
                          <Icon root="common" name="badge-info" size="small" />
                        }
                        event="hover"
                        header={''}
                        content={
                          celldata.rowData.integrationType === 'Automatic'
                            ? t(d.EDITACCOUNTDETAILSTOOLTIPFORAUTOMATIC)
                            : t(d.EDITACCOUNTDETAILSTOOLTIP)
                        }
                        position="top right"
                        size="small"
                      />
                    </VerticalMenu.Item>
                  ))}
              {getIDSPopupAction(celldata)}

              {/* <VerticalMenu.Item
                onClick={() => showDeactivateAccountModal(celldata)}
              >
                {t(d.DELETE)}
              </VerticalMenu.Item> */}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else if (celldata.rowData.status == 'Connectivity_Err') {
      return (
        <>
          <Popup
            className="emaas-utility-popup"
            position="bottom right"
            element={
              <Icon root="common" name="ellipsis-horizontal" size="medium" />
            }
            on="click"
          >
            <VerticalMenu className="ConvertToAutomatic">
              <VerticalMenu.Item onClick={() => handleActions(celldata)}>
                {t(d.REVERIFY)}
              </VerticalMenu.Item>
              {/* IDS Scenario to be handled */}
            </VerticalMenu>
          </Popup>
        </>
      );
    } else {
      return <></>;
    }
  };

  return (
    <>
      <div
        className={
          apiData && apiData.length > 0
            ? 'utility-account-datatable-container datatable-fixed-height'
            : 'utility-account-datatable-container'
        }
      >
        <DataTable
          data={apiData}
          search={true}
          searchPlaceholder={t(d.SEARCHFOR)}
          expandedRows={expandedRows}
          rowExpansionTemplate={rowExpansionTemplate as any}
          className="utility-account-datatable"
        >
          <DataTable.Column
            // initialWidth="20%"
            initialWidth="30%"
            field="providerName"
            header={t(d.SERVICEPROVIDERNAME)}
            sortable={true}
            renderer={renderServiceProviderName}
          />
          <DataTable.Column
            //initialWidth="20%"
            initialWidth="18%"
            field="accountNumber"
            header={t(d.ACCOUNTNUMBER)}
            renderer={renderAccountNumber}
          />
          <DataTable.Column
            // initialWidth="15%"
            initialWidth="16%"
            field="utilityType"
            header={t(d.UtilityType)}
            renderer={renderUtilityType}
            sortable={true}
          />
          <DataTable.Column
            initialWidth="17%"
            field="integration type"
            header={t(d.INTEGRATIONTYPENAME)}
            renderer={renderIntegrationType}
            // sortable={true}
            // initialWidth="15%"
          />
          <DataTable.Column
            // initialWidth="18%"
            initialWidth="23%"
            field="status"
            header={
              emaasContext.enableIDSEnrollment ? t(d.UDSSTATUS) : t(d.STATUS)
            }
            renderer={renderUtilityStatus}
            sortable={true}
          />
          {emaasContext.enableIDSEnrollment && (
            <DataTable.Column
              field="idsStatus"
              initialWidth="23%"
              header={t(d.IDSSTATUS)}
              renderer={renderIDSStatus}
              sortable={true}
            />
          )}
          {requestContext && requestContext.isEditAllowed && (
            <DataTable.Column
              // initialWidth="12%"
              initialWidth="11%"
              field="actions"
              header={t(d.ACTIONS)}
              renderer={renderUtilityAction}
            />
          )}
        </DataTable>
      </div>
      {apiData.length == 0 && (
        <div className="utility-box">
          <div>
            <div className="align-center">{t(d.NOACCOUNTADDEDHEADER)}</div>
            <div className="align-center">{t(d.NOACCOUNTADDEDMESSAGE)}</div>
            <div className="align-center mt-15">
              <Button
                type="primary"
                content={t(d.ADDUTILITYSERVICE)}
                className="utility-button"
                onClick={showModal}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default UtilityServiceAccountTable;
